import React, { useEffect, useState, useCallback } from "react";
import { displayName } from "@/lib/names";
import { useParams } from "react-router-dom";
import axios from "axios";
import { dateLabel, lifeLabel } from "@/lib/dates";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PersonAvatar } from "@/components/PersonAvatar";
import { AuthImg } from "@/components/AuthImg";
import { UserRoundCog, Users } from "lucide-react";

/**
 * Print-friendly version of a clan — triggers window.print() on load.
 * Hidden navbar/footer via CSS.
 */
export const PrintableClanPage = () => {
  const { key } = useParams();
  const { API } = useAuth();
  const { t, lang } = useLang();
  const [clan, setClan] = useState(null);
  const [persons, setPersons] = useState([]);

  const load = useCallback(async () => {
    const [c, p] = await Promise.all([
      axios.get(`${API}/clans/${key}`),
      axios.get(`${API}/persons`, { params: { clan_key: key } }),
    ]);
    setClan(c.data);
    setPersons(p.data);
  }, [API, key]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (clan && persons) {
      // Give the page a moment to render + images to load
      const timer = setTimeout(() => {
        try { window.print(); }
        catch (err) { console.warn("window.print() failed:", err); }
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [clan, persons]);

  if (!clan) return <div className="p-10 text-center text-[#6E5B4F]">{t("loading")}</div>;

  return (
    <div className="print-root bg-white text-[#261911]" data-testid="print-clan-page">
      <style>{`
        @page { margin: 18mm 15mm; }
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
        }
        .print-root { max-width: 900px; margin: 0 auto; padding: 32px 24px; }
        .print-root h1, .print-root h2, .print-root h3 {
          font-family: 'Cormorant Garamond', 'Merriweather', serif;
          color: #261911;
        }
        .print-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
        .print-card { border: 1px solid #D7C4B3; border-radius: 8px; padding: 12px; display: flex; gap: 10px; break-inside: avoid; }
        .label { text-transform: uppercase; letter-spacing: 0.15em; font-size: 11px; font-weight: 600; color: #6E5B4F; }
      `}</style>

      <header className="mb-10 pb-6 border-b-2" style={{ borderColor: clan.color }}>
        <div className="label" style={{ color: clan.color }}>Ekom 1 · Nkolafamba · Cameroun</div>
        <h1 className="text-5xl font-semibold mt-2" style={{ color: clan.color }}>{clan.name}</h1>
        <p className="text-sm text-[#6E5B4F] mt-2">{t("printIntro")}</p>
        <p className="text-xs text-[#6E5B4F] mt-1">{t("printDate")} {new Date().toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB")}</p>
      </header>

      {(clan.founder_name || clan.founder_story || clan.founder_photo_path) && (
        <section className="mb-10">
          <div className="label mb-3" style={{ color: clan.color }}>
            <UserRoundCog className="w-3 h-3 inline mr-1" /> {t("founder")}
          </div>
          <div style={{ display: "flex", gap: 16 }}>
            {clan.founder_photo_path && (
              <div style={{ width: 90, height: 90, borderRadius: "50%", overflow: "hidden", border: "2px solid #D7C4B3", flexShrink: 0 }}>
                <AuthImg path={clan.founder_photo_path} className="w-full h-full object-cover" alt="founder" fallback={null} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              {clan.founder_name && <h2 className="text-2xl font-semibold">{clan.founder_name}</h2>}
              {clan.founder_story && <p className="text-sm leading-relaxed mt-2 whitespace-pre-wrap">{clan.founder_story}</p>}
            </div>
          </div>
        </section>
      )}

      <section>
        <div className="label mb-3" style={{ color: clan.color }}>
          <Users className="w-3 h-3 inline mr-1" /> {t("printMembers")} ({persons.length})
        </div>
        <div className="print-grid">
          {persons.map(p => (
            <div key={p.id} className="print-card">
              <PersonAvatar person={p} size={48} />
              <div>
                <div className="font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18 }}>
                  {displayName(p)}
                </div>
                {p.nickname && <div className="text-xs italic text-[#6E5B4F]">« {p.nickname} »</div>}
                <div className="label mt-1">
                  {lifeLabel(p)}
                  {p.birth_place ? ` · ${p.birth_place}` : ""}
                </div>
                {p.biography && <p className="text-xs text-[#3A2312] mt-1 line-clamp-4">{p.biography.slice(0, 180)}{p.biography.length > 180 ? "…" : ""}</p>}
              </div>
            </div>
          ))}
        </div>
        {persons.length === 0 && (
          <p className="italic text-[#6E5B4F]">{t("noPersons")}</p>
        )}
      </section>

      <footer className="mt-10 pt-5 border-t border-[#D7C4B3] text-center text-xs text-[#6E5B4F]">
        Ekom 1 · Mémoire Evondo — Mvog Koé
      </footer>
    </div>
  );
};

export default PrintableClanPage;
